import React, { useEffect, useMemo, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import Select from "react-select"
import { createGlobalStyle } from "styled-components"
import moment from "moment"
import { useSelector } from "react-redux"
import { Button, Modal } from "react-bootstrap"
import { toast } from "react-toastify"
import { roleCheck } from "utils/index"
import { ADMIN } from "utils/constants"
import { getBaseUrl, getCurrentUser, getCurrentRole } from "../../../reducers"
import { createLeaveApplication } from "requesters/leaveApplicationRequester"
import {
  buildDefaultLeavePeriod,
  buildWorkingHoursScheduleFromUser,
  finishTimeForDate
} from "utils/dateValidation"

const DatePickerWrapperStyles = createGlobalStyle`
.date_picker.full-width {
    width: 100%;
}
`;

function apiErrorMessage(error, fallback) {
  const errs = error?.response?.data?.errors
  if (Array.isArray(errs)) {
    return errs.map(er => (typeof er === "string" ? er : er.detail || er.title)).filter(Boolean).join(" ")
  }
  return error?.message || fallback
}

function getColClasses() {
  if (typeof window !== "undefined") {
    const width = window.innerWidth
    if (width < 576) {
      return { label: "col-12 mb-1", input: "col-12" }
    }
  }
  return { label: "col-3 col-form-label", input: "col-7" }
}

export default function AnnualLeaveFormModal({ show, onHide, onSubmitted }) {
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser?.attributes?.role
  const isAdmin = roleCheck(systemRole, currentRole, ADMIN)
  const { leaveTypes } = useSelector(state => state.options)
  const workingHoursSchedule = useMemo(
    () => buildWorkingHoursScheduleFromUser(currentUser?.attributes),
    [currentUser]
  )

  const [submitting, setSubmitting] = useState(false)
  const [startAt, setStartAt] = useState(null)
  const [endAt, setEndAt] = useState(null)
  const [leaveType, setLeaveType] = useState(null)
  const [isEndAtError, setIsEndAtError] = useState(false)
  const [leaveCategoriesOptions, setLeaveCategoriesOptions] = useState([])
  const endDateRef = useRef(null)

  const colClasses = getColClasses()

  useEffect(() => {
    if (!show) return

    const { startAt: defaultStart, endAt: defaultEnd } = buildDefaultLeavePeriod(workingHoursSchedule)
    setStartAt(defaultStart)
    setEndAt(defaultEnd)
    setLeaveType(null)
    setIsEndAtError(false)
  }, [show, workingHoursSchedule])

  useEffect(() => {
    let leaveCategoriesList = Object.keys(leaveTypes || {}).map(key => ({
      value: leaveTypes[key][1],
      label: leaveTypes[key][0]
    })).sort((a, b) => a.label.localeCompare(b.label))

    if (!isAdmin) {
      leaveCategoriesList = leaveCategoriesList.filter(
        option => option.label.toLowerCase().includes("leave")
      )
    }

    setLeaveCategoriesOptions(leaveCategoriesList)
  }, [leaveTypes, isAdmin])

  const handleStartDateChange = date => {
    setStartAt(date)
    const diffInDays = moment(endAt).diff(moment(startAt), "days")
    const newEndDate = moment(date).add(diffInDays, "days")
    const { hours, minutes } = finishTimeForDate(newEndDate.toDate(), workingHoursSchedule)

    setEndAt(newEndDate.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 }).toDate())
    setIsEndAtError(false)
  }

  const onStartAtClose = () => {
    if (endDateRef.current) {
      endDateRef.current.setOpen(true)
    }
  }

  const minStartDate = (startAt ? moment(startAt) : moment()).subtract(14, "days").toDate()

  const validateForm = () => {
    if (!leaveType) {
      toast.error("Please select a reason.")
      return false
    }
    if (!startAt || !endAt) {
      toast.error("Please complete the leave period.")
      return false
    }
    if (endAt <= startAt) {
      setIsEndAtError(true)
      toast.error("The end date must be greater than the start date.")
      return false
    }

    setIsEndAtError(false)
    return true
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validateForm()) return

    setSubmitting(true)
    const res = await createLeaveApplication(baseUrl, {
      start_at: moment(startAt).format("DD/MM/YYYY HH:mm:ss"),
      end_at: moment(endAt).format("DD/MM/YYYY HH:mm:ss"),
      leave_type: leaveType.value
    })
    setSubmitting(false)

    if (res.error) {
      toast.error(apiErrorMessage(res.error, "Could not submit leave application."))
      return
    }

    toast.success("Leave application submitted.")
    onSubmitted?.()
    onHide()
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Leave Request</Modal.Title>
      </Modal.Header>
      <div className="modal-body px-md-5 px-2">
        <form onSubmit={handleSubmit}>
          <div className="form-group row">
            <label htmlFor="annual-leave-start-date" className={colClasses.label}>Start Date</label>
            <div className={colClasses.input}>
              <div className="ca-custom">
                <DatePicker
                  id="annual-leave-start-date"
                  wrapperClassName="date_picker full-width"
                  className="form-control"
                  selected={startAt}
                  onChange={handleStartDateChange}
                  onCalendarClose={onStartAtClose}
                  showTimeSelect
                  timeFormat="hh:mm aa"
                  timeIntervals={15}
                  timeCaption="time"
                  minDate={minStartDate}
                  dateFormat="dd/MM/yyyy, hh:mm aa"
                />
                <DatePickerWrapperStyles />
              </div>
            </div>
          </div>
          <div className="form-group row">
            <label htmlFor="annual-leave-end-date" className={colClasses.label}>End Date</label>
            <div className={colClasses.input}>
              <div className="ca-custom">
                <DatePicker
                  id="annual-leave-end-date"
                  ref={endDateRef}
                  wrapperClassName="date_picker full-width"
                  className="form-control"
                  selected={endAt}
                  onChange={date => {
                    setEndAt(date)
                    setIsEndAtError(false)
                  }}
                  preventOpenOnFocus={false}
                  showTimeSelect
                  timeFormat="hh:mm aa"
                  timeIntervals={15}
                  timeCaption="time"
                  minDate={startAt || new Date()}
                  dateFormat="dd/MM/yyyy, hh:mm aa"
                />
                <DatePickerWrapperStyles />
                {isEndAtError && <div className="err-msg">The end date must be greater than the start date</div>}
              </div>
            </div>
          </div>
          <div className="form-group row">
            <label htmlFor="annual-leave-reason" className={colClasses.label}>Reason</label>
            <div className={colClasses.input}>
              <Select
                id="annual-leave-reason"
                name="leaveType"
                value={leaveType}
                onChange={value => setLeaveType(value)}
                options={leaveCategoriesOptions}
                placeholder="Select a reason"
              />
            </div>
          </div>
        </form>
        <hr />
      </div>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting || !leaveType || !startAt || !endAt}
        >
          {submitting ? "Submitting…" : "Submit application"}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
